import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { motion, useInView } from "framer-motion";
import { useCountUp } from "../hooks/useCountUp";
import {
    swFade,
    swFloatUp,
    swStagger,
    swViewport,
    swZoom,
} from "../components/software/shared/softwareMotion";
import { wbsInsightMetrics, wbsKpis, wbsModules } from "../data/softwarePagesContent";
import "../styles/software-pages-base.css";
import "../styles/whatsapp-bulk-sender.css";

const KpiCard = ({
    label,
    value,
    suffix,
    active,
    className,
    decimals,
}: {
    label: string;
    value: number;
    suffix: string;
    active: boolean;
    className?: string;
    decimals?: number;
}) => {
    const scale = decimals ? Math.pow(10, decimals) : 1;
    const count = useCountUp(Math.round(value * scale), 2000, active);
    const display = decimals
        ? (count / scale).toFixed(decimals)
        : count.toLocaleString();
    return (
        <div className={`wbs-kpi ${className ?? ""}`}>
            <strong>{display}{suffix}</strong>
            <span>{label}</span>
        </div>
    );
};

const InsightMetricCard = ({
    title,
    value,
    suffix,
    desc,
    tone,
    active,
    decimals,
}: {
    title: string;
    value: number;
    suffix: string;
    desc: string;
    tone: "green" | "purple" | "violet" | "cyan";
    active: boolean;
    decimals?: number;
}) => {
    const scale = decimals ? Math.pow(10, decimals) : 1;
    const count = useCountUp(Math.round(value * scale), 2200, active);
    const display = decimals
        ? (count / scale).toFixed(decimals)
        : count.toLocaleString();

    return (
        <article className={`wbs-insights__card wbs-insights__card--${tone}`}>
            <div className="wbs-insights__card-top">
                <h3 className="wbs-insights__card-title">{title}</h3>
                <span className="wbs-insights__live-dot" aria-hidden />
            </div>
            <p className="wbs-insights__value">
                {display}
                <span className="wbs-insights__suffix">{suffix}</span>
            </p>
            <p className="wbs-insights__desc">{desc}</p>
            <div className="wbs-insights__glow" aria-hidden />
        </article>
    );
};

export const WhatsappBulkSenderPage = () => {
    const navigate = useNavigate();
    const dashRef = React.useRef<HTMLDivElement>(null);
    const insightsRef = React.useRef<HTMLDivElement>(null);
    const dashInView = useInView(dashRef, swViewport);
    const insightsInView = useInView(insightsRef, swViewport);

    return (
        <div className="sw-page wbs-page">
            {/* §1 Dashboard Preview */}
            <section className="wbs-dashboard" ref={dashRef}>
                <div className="sw-container">
                    <motion.div
                        className="wbs-dashboard__intro"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swFade}
                    >
                        <h1 className="wbs-eyebrow">WhatsApp Bulk Sender</h1>
                        <h2 className="wbs-title">Campaign command center for high-volume outreach</h2>
                        <p className="wbs-lead">
                            Launch targeted WhatsApp broadcasts with delivery analytics,
                            audience segmentation, and template compliance built in.
                        </p>
                    </motion.div>

                    <div className="wbs-dashboard__stage">
                        <motion.div
                            className="wbs-kpi wbs-kpi--float-1"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={swViewport}
                            transition={{ delay: 0.2, duration: 0.6 }}
                        >
                            <KpiCard {...wbsKpis[0]} active={dashInView} />
                        </motion.div>
                        <motion.div
                            className="wbs-kpi wbs-kpi--float-2"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={swViewport}
                            transition={{ delay: 0.35, duration: 0.6 }}
                        >
                            <KpiCard {...wbsKpis[1]} active={dashInView} />
                        </motion.div>
                        <motion.div
                            className="wbs-kpi wbs-kpi--float-3"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={swViewport}
                            transition={{ delay: 0.5, duration: 0.6 }}
                        >
                            <KpiCard {...wbsKpis[2]} active={dashInView} />
                        </motion.div>
                        <motion.div
                            className="wbs-kpi wbs-kpi--float-4"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={swViewport}
                            transition={{ delay: 0.65, duration: 0.6 }}
                        >
                            <KpiCard {...wbsKpis[3]} active={dashInView} />
                        </motion.div>

                        <motion.div
                            className="wbs-mockup"
                            initial="hidden"
                            whileInView="visible"
                            viewport={swViewport}
                            variants={swZoom}
                        >
                            <div className="wbs-mockup__bar">
                                <span>Campaigns</span>
                                <span className="wbs-mockup__pill">Live</span>
                            </div>
                            <div className="wbs-mockup__chart">
                                {[62, 48, 88, 54, 96, 72, 84].map((h, i) => (
                                    <motion.div
                                        key={i}
                                        className="wbs-mockup__bar-col"
                                        initial={{ height: 0 }}
                                        whileInView={{ height: `${h}%` }}
                                        viewport={swViewport}
                                        transition={{ delay: 0.1 * i, duration: 0.5 }}
                                    />
                                ))}
                            </div>
                            <div className="wbs-mockup__rows">
                                <div className="wbs-mockup__row"><span>Diwali Promo</span><em>12.4K sent</em></div>
                                <div className="wbs-mockup__row"><span>Cart Recovery</span><em>3.2K sent</em></div>
                                <div className="wbs-mockup__row"><span>New Arrivals</span><em>8.1K sent</em></div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* §2 Key Modules */}
            <section className="wbs-modules">
                <div className="sw-container">
                    <motion.div
                        className="wbs-section-head"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swFloatUp}
                    >
                        <h2 className="wbs-h2">Key Modules</h2>
                        <p className="wbs-sub">Everything you need to run compliant bulk campaigns.</p>
                    </motion.div>
                    <div className="wbs-modules__marquee" aria-label="Key modules carousel">
                        <div className="wbs-modules__track">
                            {[...wbsModules, ...wbsModules].map((mod, index) => {
                                const Icon = mod.icon;
                                return (
                                    <article
                                        key={`${mod.title}-${index}`}
                                        className="wbs-module"
                                    >
                                        <Icon className="wbs-module__icon" aria-hidden />
                                        <h3>{mod.title}</h3>
                                        <p className="wbs-module__teaser">Tap to explore</p>
                                        <p className="wbs-module__detail">{mod.desc}</p>
                                    </article>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* §3 Real-Time Campaign Insights */}
            <section className="wbs-insights" ref={insightsRef}>
                <div className="sw-container">
                    <motion.div
                        className="wbs-insights__head"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swFloatUp}
                    >
                        <span className="wbs-insights__badge">
                            <span className="wbs-insights__badge-dot" aria-hidden />
                            Live analytics
                        </span>
                        <h2 className="wbs-insights__title">Real-Time Campaign Insights</h2>
                        <p className="wbs-insights__subtitle">
                            See campaign performance as it happens with live delivery, engagement,
                            and audience analytics.
                        </p>
                    </motion.div>

                    <motion.div
                        className="wbs-insights__grid"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swStagger}
                    >
                        {wbsInsightMetrics.map((metric) => (
                            <motion.div key={metric.title} variants={swFloatUp}>
                                <InsightMetricCard {...metric} active={insightsInView} />
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            <section className="sw-cta">
                <div className="sw-container">
                    <div className="sw-cta__inner">
                        <h2 className="sw-cta__title">Ready to scale WhatsApp outreach?</h2>
                        <p className="sw-cta__desc">Start sending targeted bulk campaigns with delivery tracking and audience segmentation.</p>
                        <Button className="sw-btn" onClick={() => navigate("/contact")}>
                            Request a Demo <ArrowRightOutlined />
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
};
